import {
  Alert,
  Card,
  Divider,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/auth/auth-context';
import { ALLOWED_EMAIL_DOMAIN } from '@/auth/allowed-domain';
import { googleClientId, loadGoogleIdentity } from '@/auth/google-identity';
import { CenteredScreen } from '@/components/centered-screen';
import { LogoMark } from '@/components/logo';
import { ApiError, ApiErrorCode, NetworkError } from '@/lib/api-error';
import classes from './sign-in.module.css';

/** Turns a failure into something an operator can act on. */
function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'Could not reach the Fluently API. Check that it is running.';
  }

  if (error instanceof ApiError) {
    // The API already writes operator-facing messages for the cases it can
    // anticipate; passing them through avoids two sources of truth that drift.
    switch (error.code) {
      case ApiErrorCode.DOMAIN_NOT_ALLOWED:
      case ApiErrorCode.NOT_AUTHORIZED_FOR_ADMIN:
      case ApiErrorCode.INVALID_GOOGLE_TOKEN:
      case ApiErrorCode.SSO_NOT_CONFIGURED:
        return error.message;
      default:
        return 'Sign-in failed. Please try again.';
    }
  }

  return error instanceof Error ? error.message : 'Sign-in failed.';
}

export function SignInPage() {
  const { user, isLoading, signInWithGoogleCredential } = useAuth();
  const location = useLocation();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isExchanging, setIsExchanging] = useState(false);
  const clientId = googleClientId();
  const busy = isLoading || isExchanging;

  const handleCredential = useCallback(
    (idToken: string) => {
      setError(null);
      setIsExchanging(true);
      void signInWithGoogleCredential(idToken)
        .catch((cause: unknown) => setError(describeError(cause)))
        .finally(() => setIsExchanging(false));
    },
    [signInWithGoogleCredential],
  );

  // The container below stays mounted for the whole life of this screen, even
  // while the session check is in flight. An earlier version rendered it only
  // once loading finished, so on first render the ref was null and this effect
  // bailed — and since `isLoading` was not a dependency, it never re-ran once
  // the element appeared. The button silently never rendered.
  useEffect(() => {
    if (!clientId || !buttonRef.current) return;
    let cancelled = false;

    loadGoogleIdentity()
      .then((gis) => {
        if (cancelled || !buttonRef.current) return;

        gis.initialize({
          client_id: clientId,
          callback: (response) => handleCredential(response.credential),
          // No auto-select: an admin portal over production data should require
          // a deliberate act to sign in, not resume silently.
          auto_select: false,
        });

        gis.renderButton(buttonRef.current, {
          type: 'standard',
          // `outline` in both colour schemes, deliberately.
          //
          // Google's dark themes look like the obvious choice on the navy card,
          // but GIS has two rendering paths and picks between them itself: an
          // inline one, and a cross-origin iframe whose document background is
          // white. On the iframe path — which is what runs here — `filled_blue`
          // and `filled_black` are drawn as a coloured pill on a white plate,
          // and the plate cannot be reached to restyle. `outline` is white
          // anyway, so it is the one theme that survives both paths intact.
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width: 360,
        });
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(describeError(cause));
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, handleCredential]);

  // Already signed in — bounce to wherever they were originally headed.
  if (user) {
    const from = (location.state as { from?: { pathname: string } } | null)?.from;
    return <Navigate to={from?.pathname ?? '/'} replace />;
  }

  return (
    <CenteredScreen gradient>
      <Card shadow="md" padding="xl" className={classes.card}>
        <Stack gap="lg">
          <Stack gap="xs" align="center">
            <LogoMark size={56} />
            <Stack gap={4} align="center">
              <Title order={3}>Fluently Admin</Title>
              <Text size="sm" c="dimmed" ta="center">
                Sign in with your {ALLOWED_EMAIL_DOMAIN} Google account.
              </Text>
            </Stack>
          </Stack>

          {error && (
            <Alert color="red" title="Cannot sign in" icon={<IconAlertTriangle size={18} />}>
              {error}
            </Alert>
          )}

          {!clientId && (
            <Alert
              color="yellow"
              title="Not configured"
              icon={<IconInfoCircle size={18} />}
            >
              VITE_GOOGLE_CLIENT_ID is not set in this build, so Google sign-in is
              unavailable. See ADR 0003.
            </Alert>
          )}

          <Divider label="Continue with" labelPosition="center" />

          {/* Google renders its own button here; it cannot be replaced with a
              Mantine component without breaking Google's branding terms. Kept
              mounted at all times so the ref is stable — hidden rather than
              unmounted while busy. */}
          <div className={classes.googleButton}>
            {busy ? <Loader size="sm" /> : null}
            <div ref={buttonRef} hidden={busy} />
          </div>
        </Stack>
      </Card>

      <Text size="xs" c="dimmed" mt="lg" ta="center">
        Internal tool. Access is restricted and every request is authorised by the API.
      </Text>
    </CenteredScreen>
  );
}
